import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { formatMoney } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { useUrlFilters } from '../lib/useUrlFilters';
import type { DealItem } from '../types';

function scoreClass(score: number): string {
  if (score >= 80) return 'badge-score-hi';
  if (score >= 60) return 'badge-score-mid';
  return 'badge-score-lo';
}

export function Deals() {
  const [params, setParams] = useUrlFilters();
  const store = params.get('store') ?? '';

  // Input numérico tipeable: draft local + debounce antes de escribir la URL.
  const urlMinScore = params.get('minScore') ?? '70';
  const [minScoreDraft, setMinScoreDraft] = useState(urlMinScore);
  const debouncedMinScore = useDebouncedValue(minScoreDraft, 400);

  useEffect(() => {
    if (debouncedMinScore === '') return;
    setParams(
      { minScore: debouncedMinScore === '70' ? '' : debouncedMinScore },
      { replace: true },
    );
  }, [debouncedMinScore, setParams]);

  useEffect(() => {
    if (urlMinScore !== debouncedMinScore) setMinScoreDraft(urlMinScore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMinScore]);

  const minScore = Number(urlMinScore) || 70;

  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.stores() });
  const deals = useQuery({
    queryKey: ['deals', { minScore, store }],
    queryFn: () => api.deals({ minScore, store: store || undefined, limit: 60 }),
  });

  return (
    <>
      <div className="toolbar">
        <label>
          Score mínimo
          <input
            type="number"
            min={0}
            max={100}
            value={minScoreDraft}
            onChange={(e) => setMinScoreDraft(e.target.value)}
            style={{ marginLeft: 8, width: 80 }}
          />
        </label>
        <select value={store} onChange={(e) => setParams({ store: e.target.value })}>
          <option value="">Todas las tiendas</option>
          {stores.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
          80+ descuento real fuerte · 60–79 atención · &lt;60 nada especial
        </span>
      </div>

      {deals.isLoading && <div className="loading">Cargando…</div>}
      {deals.isError && <div className="error">Error: {(deals.error as Error).message}</div>}
      {deals.data && deals.data.length === 0 && (
        <div className="empty">Sin descuentos reales con ese umbral.</div>
      )}

      <div className="grid">
        {deals.data?.map((d) => (
          <DealCard key={d.productId} deal={d} />
        ))}
      </div>
    </>
  );
}

function DealCard({ deal }: { deal: DealItem }) {
  return (
    <Link to={`/p/${deal.productId}`} className="card">
      {deal.imageUrl ? (
        <img className="card-img" src={deal.imageUrl} alt="" loading="lazy" />
      ) : (
        <div className="card-img-fallback">sin imagen</div>
      )}
      <div className="card-name" title={deal.name}>
        {deal.name}
      </div>
      <div className="card-price-row">
        <span className="card-price">{formatMoney(deal.currentPriceCents)}</span>
        <span className={`badge badge-score ${scoreClass(deal.dealScore)}`}>
          {deal.dealScore}
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{deal.reason}</div>
      <div className="card-foot">
        <span>{deal.storeId}</span>
        <span>min 90d {formatMoney(deal.min90dCents)}</span>
        <span>med 180d {formatMoney(deal.median180dCents)}</span>
      </div>
    </Link>
  );
}
